import { useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, CircularProgress, Stack } from '@mui/material';
import { Visibility, Send as SendIcon } from '@mui/icons-material';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import InlineMessageComposer from '../../components/InlineMessageComposer';
import DrillDownPanel from '../../components/DrillDownPanel';
import ClickableName from '../../components/ClickableName';
import ActionMenu from '../../components/ActionMenu';
import ConversionFunnel from './ConversionFunnel';
import ConversionSpeed from './ConversionSpeed';

import { getConversionAnalytics, getFunnelDrillDown } from 'api/rest/analytics';
import { DimensionType, FunnelDropOffItem } from 'hooks/analytics';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import useAnalyticsActions from '../../hooks/useAnalyticsActions';
import type { AnalyticsOutletContext } from '../../AnalyticsShell';

const TRANSITION_LABELS: Record<string, string> = {
    registeredToAssessment: 'Registered \u2192 Assessment',
    assessmentToOffer: 'Assessment \u2192 Offer',
    offerToPaid: 'Offer \u2192 Paid',
};

const ConversionView = () => {
    const { isOwner, from, to, serviceProviderId } = useOutletContext<AnalyticsOutletContext>();
    const { data, loading, error, refetch } = useAnalyticsData(
        () => getConversionAnalytics(from, to, serviceProviderId),
        [from, to, serviceProviderId]
    );
    const actions = useAnalyticsActions(refetch);
    const [dimension, setDimension] = useState<DimensionType>('all');
    const [drillDown, setDrillDown] = useState<{ key: string; items: FunnelDropOffItem[] } | null>(
        null
    );
    const [drillDownLoading, setDrillDownLoading] = useState(false);

    const handleTransitionClick = useCallback(
        async (transitionKey: string) => {
            setDrillDownLoading(true);
            try {
                const res = await getFunnelDrillDown(transitionKey, from, to, serviceProviderId);
                setDrillDown({ key: transitionKey, items: res.data });
            } catch {
                setDrillDown({ key: transitionKey, items: [] });
            } finally {
                setDrillDownLoading(false);
            }
        },
        [from, to, serviceProviderId]
    );

    if (loading) {
        return <AnalyticsLoading />;
    }

    if (error || !data) {
        return (
            <AnalyticsError
                message={error || 'Failed to load conversion data. Please try again.'}
            />
        );
    }

    const dimensionData =
        dimension === 'program'
            ? data.byProgram
            : dimension === 'country'
              ? data.byCountry
              : dimension === 'practitioner'
                ? data.byPractitioner
                : null;

    return (
        <Stack spacing={2}>
            <ConversionFunnel
                current={data.current}
                comparison={data.comparison}
                dimension={dimension}
                onDimensionChange={setDimension}
                dimensionData={dimensionData}
                isOwner={isOwner}
                onTransitionClick={handleTransitionClick}
            />
            <ConversionSpeed current={data.current.speed} comparison={data.comparison.speed} />

            {drillDownLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {drillDown && !drillDownLoading && (
                <DrillDownPanel<FunnelDropOffItem>
                    open
                    onClose={() => setDrillDown(null)}
                    title={`Drop-Off: ${TRANSITION_LABELS[drillDown.key] || drillDown.key}`}
                    subtitle={`${drillDown.items.length} clients stalled at this transition`}
                    items={drillDown.items}
                    columns={[
                        {
                            label: 'Client',
                            render: (item) => (
                                <ClickableName
                                    onClick={() => actions.navigateToClient(item.clientId)}
                                >
                                    {item.candidateName}
                                </ClickableName>
                            ),
                        },
                        { label: 'Program', render: (item) => item.programName },
                        { label: 'Consultant', render: (item) => item.consultantName },
                        {
                            label: 'Days Stalled',
                            align: 'right',
                            render: (item) => (
                                <span
                                    style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {item.daysSinceLastProgress}d
                                </span>
                            ),
                        },
                    ]}
                    renderActions={(item) => (
                        <ActionMenu
                            isOwner={isOwner}
                            items={[
                                {
                                    label: 'View Case',
                                    icon: <Visibility sx={{ fontSize: 14 }} />,
                                    onClick: () => actions.navigateToClient(item.clientId),
                                },
                                ...(item.chatGroupId
                                    ? [
                                          {
                                              label: 'Follow Up',
                                              icon: <SendIcon sx={{ fontSize: 14 }} />,
                                              onClick: () =>
                                                  actions.openMessageComposer(
                                                      item.chatGroupId,
                                                      item.candidateName,
                                                      'your application progress'
                                                  ),
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    )}
                />
            )}

            {actions.confirmDialog.open && (
                <ConfirmationDialog
                    title={actions.confirmDialog.title}
                    content={actions.confirmDialog.content}
                    loading={actions.confirmDialog.loading}
                    onClose={actions.closeConfirmDialog}
                    onConfirm={actions.confirmDialog.onConfirm}
                />
            )}

            <InlineMessageComposer
                open={actions.messageComposer.open}
                recipientName={actions.messageComposer.recipientName}
                template={actions.messageComposer.template}
                onClose={actions.closeMessageComposer}
                onSend={actions.sendMessage}
            />
        </Stack>
    );
};

export default ConversionView;
